import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE,
    TYPE_A, TYPE_B,
    PROVEN_NO, PROVEN_IN_USE, PRIOR_USE,
} from "const"
import { QidModelSafetyFunction, QidModelSafetyFunctionCreate } from "server/models"
import {
    useInput,
    StateType,
} from "client/utils"
import {
    StoreType,
    QidModelSafetyFunctionValidation,
    SaveQidModelSafetyFunction,
    closeModal,
    setQidModelEditSafetyFunction,
    setQidModelReloadList,
    setQidModelReloadEditSafetyFunction,
    setQidDatapointReloadList,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    RadioButton,
    CheckButton,
    CheckboxWrapper,
    Button,
    SpinnerIcon,
} from "client/components"

interface QidModelSafetyFunctionFormProps {
    safetyFunction: QidModelSafetyFunction
}

let isMounted: boolean

const Form = ({
    safetyFunction,
}: QidModelSafetyFunctionFormProps) => {
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [modelId] = useState(safetyFunction.modelId)

    const [name, setName] = useInput(safetyFunction.name)
    const [type, setType] = useState(safetyFunction.type)
    const [lowDemandMode, setLowDemandMode] = useState(safetyFunction.lowDemandMode)
    const [highDemandMode, setHighDemandMode] = useState(safetyFunction.highDemandMode)
    const [continuousMode, setContinuousMode] = useState(safetyFunction.continuousMode)
    const [logicSolver, setLogicSolver] = useState(safetyFunction.logicSolver)
    const [provenInUse, setProvenInUse] = useState(safetyFunction.provenInUse)

    const [validation, setValidation] = useState<QidModelSafetyFunctionValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return
        if (isMounted && modals.length) {
            dispatch(closeModal())
        }
    }

    const onSave = (saveMode: number) => {
        if (saveMode === 1) setSaveState(PENDING)
        if (saveMode === 2) setApplyState(PENDING)

        const newSafetyFunctionData: QidModelSafetyFunctionCreate = {
            modelId,
            name,
            type,
            lowDemandMode,
            highDemandMode,
            continuousMode,
            logicSolver,
            provenInUse,
        }

        const updated = safetyFunction && safetyFunction.id && (
            safetyFunction.name !== name
            || safetyFunction.type !== type
            || safetyFunction.lowDemandMode !== lowDemandMode
            || safetyFunction.highDemandMode !== highDemandMode
            || safetyFunction.continuousMode !== continuousMode
            || safetyFunction.logicSolver !== logicSolver
            || safetyFunction.provenInUse !== provenInUse
        )

        SaveQidModelSafetyFunction(
            safetyFunction.id,
            newSafetyFunctionData,
            updated,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res.validation) return

            dispatch(setQidModelEditSafetyFunction({
                id: res.newSafetyFunctionId || safetyFunction.id,
                ...newSafetyFunctionData,
            }))

            dispatch(setQidModelReloadList(false))
            dispatch(setQidDatapointReloadList(false))
            setTimeout(() => {
                dispatch(setQidModelReloadList(true))
                dispatch(setQidDatapointReloadList(true))
            }, 0)

            if (saveMode === 1) onClose()
            if (saveMode === 2) {
                dispatch(setQidModelReloadEditSafetyFunction(false))
                setTimeout(() => {
                    dispatch(setQidModelReloadEditSafetyFunction(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.mode}</Label>
                        <CheckboxWrapper>
                            <CheckButton
                                checked={lowDemandMode}
                                onChange={() => setLowDemandMode(!lowDemandMode)}
                                title={lang.modeLowDemand}
                            />
                            <CheckButton
                                checked={highDemandMode}
                                onChange={() => setHighDemandMode(!highDemandMode)}
                                title={lang.modeHighDemand}
                            />
                            <CheckButton
                                checked={continuousMode}
                                onChange={() => setContinuousMode(!continuousMode)}
                                title={lang.modeContinuous}
                            />
                        </CheckboxWrapper>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.type}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={type === TYPE_A}
                                onChange={() => setType(TYPE_A)}
                                title={TYPE_A}
                            />
                            <RadioButton
                                checked={type === TYPE_B}
                                onChange={() => setType(TYPE_B)}
                                title={TYPE_B}
                            />
                        </FormGroup>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.logicSolver}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={logicSolver}
                                onChange={() => setLogicSolver(true)}
                                title={lang.yes}
                            />
                            <RadioButton
                                checked={!logicSolver}
                                onChange={() => setLogicSolver(false)}
                                title={lang.no}
                            />
                        </FormGroup>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.provenReliabilityData}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={provenInUse === PROVEN_NO}
                                onChange={() => setProvenInUse(PROVEN_NO)}
                                title={lang.no}
                            />
                            <RadioButton
                                checked={provenInUse === PROVEN_IN_USE}
                                onChange={() => setProvenInUse(PROVEN_IN_USE)}
                                title={lang.provenInUse}
                            />
                            <RadioButton
                                checked={provenInUse === PRIOR_USE}
                                onChange={() => setProvenInUse(PRIOR_USE)}
                                title={lang.priorUse}
                            />
                        </FormGroup>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
